import axios, {AxiosError, AxiosRequestConfig, AxiosRequestHeaders} from 'axios';
import {isBoolean, pickBy} from 'lodash';
import {Store} from 'redux';

import {isMobile} from '../../../libs/shared/helpers/src/environment/mobileEnvHelpers';
import {
  annotateTaskType,
  TaskTypesEnum,
} from '../../../libs/shared/helpers/src/instrumentation/eventLoopPerformanceHelpers';
import {Handler, HandlerOf} from '../../../libs/shared/helpers/src/react/reactHelpers';
import {hasProperty, isDefined} from '../../../libs/shared/helpers/src/typescript/typeHelpers';
import {FrontConfig} from '../../../srcCommon/bundler/bundlerDefineConfig';
import {httpDefaultHeaders, httpUserAgent} from '../../../srcCommon/helpers/httpHelpers';
import {logger} from '../../../srcCommon/services/logger/logger';
import {CancelError, CancelToken} from '../../helpers/asyncCancelHelpers';
import {AppState} from '../../store/appState';
import {getBuildVersion} from '../buildVersion';
import {trace} from '../debug';
import {sessionId} from '../session';
import {HttpNetworkError, HttpResponseError, HttpTimeoutError} from './httpError';

/*
 * HACK: Find better solution.
 */

type PushGlobalState = {
  defaultSocketId: string | null;
  ablySocketId: string | null;
};
export const pushState: PushGlobalState = {defaultSocketId: null, ablySocketId: null};

/*
 * Types.
 */

export enum RequestMethodsEnum {
  GET = 'GET',
  POST = 'POST',
  PUT = 'PUT',
  PATCH = 'PATCH',
  DELETE = 'DELETE',
}

export interface HttpHeaders {
  [T: string]: string;
}

export type ResponseHeaders = Record<string, string | undefined>;

export interface HttpErrorHandlers {
  onUnauthorized: Handler;
  onSessionTimeout: Handler;
  onSessionsMaxReached: Handler;
}

export interface ServerResponseInfo {
  headers: ResponseHeaders;
}

export interface HttpSendOptions {
  cancelToken?: CancelToken;
  headers?: HttpHeaders;
  errorHandlers?: HttpErrorHandlers;
  onProgressChanged?: HandlerOf<ProgressEvent>;
  onResponseReceived?: HandlerOf<ServerResponseInfo>;
  timeout?: number;
}

export interface HttpFullSendOptions extends HttpSendOptions {
  method: RequestMethodsEnum;
  path: string;
  body?: unknown;
}

export interface HttpResponse {
  status: number;
  headers: ResponseHeaders;
  data: unknown;
}

/*
 * Setup.
 */

let authenticationToken: string | undefined;
export function setHttpToken(token: string | undefined) {
  authenticationToken = token;
}

export function httpToken() {
  return authenticationToken;
}

let baseUrl: string | undefined;
export function setBaseUrl(newBaseUrl: string | undefined) {
  baseUrl = newBaseUrl;
}

let isTracingPerformance = false;

export function setupHttp(store: Store<AppState>) {
  store.subscribe(() => {
    const state = store.getState();
    isTracingPerformance = state.data.localPreferences.flags.TRACE_PERFORMANCE;
  });
}

const requester = axios.create({
  baseURL: FrontConfig.API_URL,
  withCredentials: true,
  xsrfCookieName: 'front.csrf',
  xsrfHeaderName: 'X-Front-Xsrf',
  headers: httpDefaultHeaders,
});

/*
 * API.
 */

/** Perform a generic HTTP method. */
export async function sendAsync(options: HttpFullSendOptions): Promise<HttpResponse> {
  // Build the options for our request.
  const majorBuildVersion = getBuildVersion();
  const headers: AxiosRequestHeaders = pickBy(
    {
      Accept: 'application/json',
      Authorization: authenticationToken ? `Bearer ${authenticationToken}` : undefined,
      'user-agent': httpUserAgent,
      'X-Front-Version': `${majorBuildVersion}-${FrontConfig.GIT_COMMIT}`,
      'X-Front-Trace-Perf': isTracingPerformance ? 'yes' : 'no',
      'X-Front-Webapp-Build-Timestamp': FrontConfig.BUILD_TIMESTAMP,
    },
    isDefined,
  );
  const {method, path, body, cancelToken, timeout, errorHandlers} = options;

  if (isMobile()) {
    logger.debug('Performing request:', {path: options.path});
  }

  const abortController = new AbortController();
  const cancelSubscription = cancelToken?.subscribe(() => abortController.abort());

  const requestStartTime = Date.now();
  const timeoutDelay = timeout || 30000;

  const axiosOptions: AxiosRequestConfig = {
    baseURL: baseUrl,
    url: path,
    method,
    headers,
    signal: abortController.signal,
    onUploadProgress: (e) => onProgress(e, options),
    onDownloadProgress: (e) => onProgress(e, options),
    timeout: timeoutDelay,
  };

  // Axios' timeout is not completely reliable, so we made our own.
  let didTimeout = false;
  const onRequestTimeout = setTimeout(() => {
    didTimeout = true;
    abortController.abort();
  }, timeoutDelay);

  // If a body was provided, add it.
  if (body) {
    axiosOptions.data = body;
  }

  // If needed, add the WebSocketId header.
  if (pushState.defaultSocketId) {
    headers['X-Front-WebSocket-Id'] = pushState.defaultSocketId;
  }

  if (pushState.ablySocketId) {
    headers['X-Front-Ably-WebSocket-Id'] = pushState.ablySocketId;
  }

  // This precog is also sent directly to Pusher. This boolean allows clients to ignore it safely if they support receiving direct precogs.
  // See https://github.com/frontapp/front-client/pull/3599.
  headers['x-front-precogs'] = 'direct';
  headers['X-Front-Session-Id'] = sessionId;

  Object.assign(headers, options.headers);

  trace(`http-${method}-start`, {path, body});

  try {
    // Perform the request.
    const response = await requester.request(axiosOptions);

    trace(`http-${method}-end`, {path, body});
    annotateTaskType(TaskTypesEnum.NETWORK_RESPONSE);

    if (options.onResponseReceived) {
      options.onResponseReceived({
        headers: response.headers || {},
      });
    }

    return response;
  } catch (error) {
    if (!axios.isAxiosError(error)) {
      throw error;
    }

    if (didTimeout || isAxiosTimeoutError(error)) {
      throw new HttpTimeoutError(method, path, Date.now() - requestStartTime);
    }

    // If this is a cancel error, return our own.
    if (axios.isCancel(error) || isAxiosAbortedError(error)) {
      throw new CancelError();
    }

    // If this is an XHR network error, wrap in our own class.
    // https://github.com/axios/axios/blob/master/lib/adapters/xhr.js#L81
    if (isAxiosNetworkError(error)) {
      throw new HttpNetworkError(method, path);
    }

    trace(`http-${method}-error`, {path, body, error});

    if (errorHandlers && error.response?.status === 401) {
      if (hasProperty(error.response.data, 'code') && error.response.data.code === 'session_idle_timeout') {
        errorHandlers.onSessionTimeout();
      } else if (
        hasProperty(error.response.data, 'code') &&
        error.response.data.code === 'session_max_limit'
      ) {
        errorHandlers.onSessionsMaxReached();
      } else {
        errorHandlers.onUnauthorized();
      }
    }

    // If this is an HTTP error, wrap in our own class.
    // Check the status to work around https://github.com/axios/axios/issues/4655
    if (error.response?.status) {
      throw new HttpResponseError(method, path, error.response);
    }

    // No idea what this is, so let's rethrow.
    throw error;
  } finally {
    cancelSubscription?.unsubscribe();
    clearTimeout(onRequestTimeout);
  }
}

function isProgressEvent(src: any): src is ProgressEvent {
  if (typeof ProgressEvent !== 'undefined') {
    return src instanceof ProgressEvent;
  }

  // React Native doesn't expose ProgressEvent so we can't always rely on instanceof.
  return isBoolean(src.lengthComputable) && Number.isFinite(src.loaded) && Number.isFinite(src.total);
}

function onProgress(event: any, options: HttpFullSendOptions) {
  const {onProgressChanged} = options;
  if (!onProgressChanged) {
    return;
  }

  if (!isProgressEvent(event)) {
    return;
  }

  onProgressChanged(event);
}

/*
 * Spying.
 */

export function spyOnHttpResponses(
  onResponse: HandlerOf<HttpResponse>,
  onNetworkError?: HandlerOf<HttpFullSendOptions | undefined>,
) {
  const interceptor = requester.interceptors.response.use(
    (response) => {
      onResponse(response);
      return response;
    },
    (error) => {
      // Check the status to work around https://github.com/axios/axios/issues/4655
      if (error.response?.status) {
        onResponse(error.response);
      } else if (onNetworkError && (isAxiosNetworkError(error) || isAxiosTimeoutError(error))) {
        onNetworkError(createSendOptionsFromError(error));
      }

      return Promise.reject(error);
    },
  );
  return () => requester.interceptors.response.eject(interceptor);
}

/*
 * Helpers.
 */

/** Detects the error thrown when the browser cancels the request. */
export function isAxiosAbortedError(error: AxiosError) {
  return error.code === AxiosError.ECONNABORTED && !error.message.startsWith('timeout');
}

export function isAxiosNetworkError(error: AxiosError) {
  return error.code === AxiosError.ERR_NETWORK;
}

export function isAxiosTimeoutError(error: AxiosError) {
  return (
    error.code === AxiosError.ETIMEDOUT ||
    (error.code === AxiosError.ECONNABORTED && error.message.startsWith('timeout'))
  );
}

function createSendOptionsFromError(error: unknown): HttpFullSendOptions | undefined {
  if (!axios.isAxiosError(error)) {
    return undefined;
  }

  const {method, url} = error.config;
  if (!method || !url) {
    return undefined;
  }

  return {
    method: method.toUpperCase() as RequestMethodsEnum,
    path: url,
  };
}
